<script lang="ts" setup>
import { useEcho } from '@laravel/echo-vue'
import { computed, ref } from 'vue'
import Group from '~/components/gamescreen/Group.vue'

const { $ts: t } = useNuxtApp()

const { tournament } = defineProps<{
  tournament: Tournament
}>()

const { data: groups } = await useApi('/tournaments/{tournament}/groups', {
  path: {
    tournament: tournament.id,
  },
  key: `tournament-groups-${tournament.id}`,
  deep: true,
})

if (groups.value === null || groups.value === undefined) {
  throw new Error('No groups phase.')
}

useSeoMeta({
  title: `${t('Groups')} | ${tournament.name} @ ${tournament.event_name}`,
})

defineOgImageComponent('DartsSpace', {
  title: `${t('Groups')} | ${tournament.name}`,
})

useEcho<TournamentGroup>(
  `tournaments.${tournament.id}`,
  '.App\\Events\\GroupUpdatedEvent',
  (newData) => {
    if (groups.value && groups.value[newData.group_number]) {
      groups.value[newData.group_number] = newData
    }
  },
)

useEcho<Game>(
  `tournaments.${tournament.id}`,
  '.App\\Events\\GameUpdatedEvent',
  (newData) => {
    if (groups.value && newData.group && groups.value[newData.group]) {
      const index = groups.value[newData.group]!.games.findIndex(item => item.id === newData.id)
      groups.value[newData.group]!.games[index] = newData
    }
  },
)

// Chunk groups into sets of 10 if needed
const groupChunks = computed(() => {
  if (!groups.value) return []
  const arr = Object.values(groups.value)
  const chunks = []
  for (let i = 0; i < arr.length; i += 10) {
    chunks.push(arr.slice(i, i + 10))
  }
  return chunks
})

const activeTab = ref('0')
</script>

<template>
  <div v-if="tournament.phase === 'group' || tournament.phase === 'elimination' || tournament.complete">
    <!-- If more than 16 groups, use tabs -->
    <template v-if="Object.keys(groups ?? {}).length > 16">
      <UiTabs v-model="activeTab">
        <UiTabsList>
          <UiTabsTrigger
            v-for="(chunk, idx) in groupChunks"
            :key="idx"
            :value="String(idx)"
          >
            {{ $t('Groups') }} {{ idx * 10 + 1 }}-{{ Math.min((idx + 1) * 10, Object.keys(groups ?? {}).length) }}
          </UiTabsTrigger>
        </UiTabsList>
        <UiTabsContent
          v-for="(chunk, idx) in groupChunks"
          :key="'content-' + idx"
          :value="String(idx)"
        >
          <div class="grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 md:gap-2 self-start w-full">
            <Group
              v-for="group in chunk"
              :key="group.group_number"
              :boards="[]"
              :tournament="tournament"
              :group="group"
              :display-only="true"
            />
          </div>
        </UiTabsContent>
      </UiTabs>
    </template>
    <!-- Otherwise, render all groups as before -->
    <template v-else>
      <div class="grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 md:gap-2 self-start w-full">
        <Group
          v-for="group in groups"
          :key="group.group_number"
          :boards="[]"
          :tournament="tournament"
          :group="group"
          :display-only="true"
        />
      </div>
    </template>
  </div>
  <div v-else>
    <UiAlert
      variant="warning"
      :title="$t('Group phase has not started')"
    />
  </div>
</template>
